import { IconButton } from '@material-ui/core';
import cx from 'classnames';
import T from 'components/common/text/T';
import React, { ReactNode, useContext, useEffect } from 'react';
import styled from 'styled-components';
import { color } from 'styles/utils';
import { icon_back_large, icon_chat_on, icon_chat_off, icon_gold_state } from '../images';
import { PrivateSocketContext } from 'contexts/PrivateSocketContext';
import { ProjectSocketContext } from 'contexts/ProjectSocketContext';
import { getCookie, keys } from 'lib/storage';
import { oemState } from 'lib/store';

export interface PageTitleProps {
  title: string | ReactNode;
  margin?: string;
  className?: string;
}

interface PageTitleGoBackProps extends PageTitleProps {
  date?: string;
  timelineLegends?: {
    owner: string;
    partner: string;
  };
  useChat?: boolean;
  useGoldState?: boolean;
  isShowChat?: boolean;
  goBack?: () => void;
  onClickChat?: () => void;
  onClickGold?: () => void;
}

export default function PageTitle({ title, className }: PageTitleProps) {
  return (
    <Styled.PageTitle data-component-name="PageTitle">
      <h1 className={cx('pageTitle__container', className)}>
        <div className={`pageTitle__container_title ${oemState()}`}>
          {typeof title === 'string' ? <T>{title}</T> : { title }}
        </div>
      </h1>
    </Styled.PageTitle>
  );
}

export function PageSubTitle({ title, className, margin = '20px 0' }: PageTitleProps) {
  return (
    <Styled.PageSubTitle data-component-name="PageSubTitle" $margin={margin}>
      <h1 className={cx('pageSubTitle__container', className)}>
        <div className={`pageSubTitle__container_title  ${oemState()}`}>
          {typeof title === 'string' ? <T>{title}</T> : title}
        </div>
      </h1>
    </Styled.PageSubTitle>
  );
}

export function PageTitleGoBack({
  title,
  className,
  date = '',
  useChat = false,
  useGoldState = false,
  isShowChat = false,
  goBack,
  timelineLegends,
  onClickChat,
  onClickGold,
}: PageTitleGoBackProps) {
  const { newProjectChat } = useContext(ProjectSocketContext);

  return (
    <Styled.PageTitleGoBack data-component-name="PageTitleGoBack">
      <h1 className={cx('pageTitleGoBack__container', className)}>
        <IconButton className="pageTitleGoBack__goback_icon" onClick={goBack}>
          <img src={icon_back_large} color={oemState() === 'marathon' ? '#fff' : 'inherit'} />
        </IconButton>
        <div className="pageTitleGoBack__box">
          <div className="pageTitleGoBack__title_box">
            <span className={`pageTitleGoBack__title ${oemState()}`}>
              {typeof title === 'string' ? <T>{title}</T> : { title }}
            </span>
            <span className="pageTitleGoBack__date">{date}</span>
          </div>
          {!!timelineLegends && (
            <>
              <div className="pageTitleGoBack__division_line"></div>
              <div className="pageTitleGoBack__legends_box">
                <div className="pageTitleGoBack__legends_item">
                  <div className="pageTitleGoBack__legends_icon owner"></div>
                  <div className="pageTitleGoBack__legends_label owner">
                    {timelineLegends?.owner}
                  </div>
                </div>
                <div className="pageTitleGoBack__legends_item">
                  <div className="pageTitleGoBack__legends_icon partner"></div>
                  <div className="pageTitleGoBack__legends_label partner">
                    {timelineLegends?.partner}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </h1>
      <div className="pageTitleGoBack__icon_box">
        {useGoldState && (
          <div className="pageTitleGoBack__icon gold" onClick={onClickGold}>
            <img src={icon_gold_state} />
          </div>
        )}
        {useChat && (
          <div className="pageTitleGoBack__icon chat" onClick={onClickChat}>
            {isShowChat ? <img src={icon_chat_on} /> : <img src={icon_chat_off} />}
            {!isShowChat && newProjectChat.value > 0 && (
              <span className="chat_count">{newProjectChat.value}</span>
            )}
          </div>
        )}
      </div>
    </Styled.PageTitleGoBack>
  );
}

const Styled = {
  PageTitle: styled.div<{}>`
    width: 100%;
    height: 70px;
    display: flex;
    align-items: center;

    .pageTitle__container_title {
      height: 38px;
      padding-left: 20px;
      font-size: 36px;
      letter-spacing: -0.72px;
      &.sync {
        color: #222;
        border-left: 4px solid #25256f;
      }
      &.marathon {
        color: #fff;
        border-left: 4px solid #fff;
      }
    }
  `,
  PageSubTitle: styled.div<{ $margin: string }>`
    height: 30px;
    margin: ${({ $margin }) => $margin};
    display: flex;
    align-items: center;
    .pageSubTitle__container_title {
      line-height: 30px;
      font-size: 24px;
      font-weight: 500;
      &.sync {
        color: #222;
      }
      &.marathon {
        color: #fff;
      }
    }
  `,

  PageTitleGoBack: styled.div<{}>`
    /* width: calc(100% - 330px); */
    width: calc(100% - 285px);
    /* margin-right: -24px; */
    height: 70px;
    position: relative;
    z-index: 100;
    display: flex;
    align-items: center;
    justify-content: space-between;

    .pageTitleGoBack__container {
      width: 100%;
      height: inherit;
      display: flex;
      align-items: center;
      .pageTitleGoBack__goback_icon {
        padding: 5px !important;
        font-size: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
        img {
          width: 34px;
          height: 34px;
        }
      }

      .pageTitleGoBack__box {
        display: flex;
        align-items: center;
        .pageTitleGoBack__title_box {
          display: flex;
          align-items: center;
          .pageTitleGoBack__title {
            line-height: 30px;
            font-size: 24px;
            font-weight: 600;
            margin-left: 10px;
            margin-right: 30px;
            &.sync {
              color: #222;
            }
            &.marathon {
              color: #fff;
            }
          }
          .pageTitleGoBack__date {
            font-size: 14px;
            color: #888;
            font-weight: 400;
          }
        }

        .pageTitleGoBack__division_line {
          width: 1px;
          background-color: #e1e1e1;
          height: 26px;
          margin: 0 30px;
        }

        .pageTitleGoBack__legends_box {
          display: flex;
          column-gap: 20px;
          .pageTitleGoBack__legends_item {
            display: flex;
            align-items: center;
            column-gap: 10px;
            .pageTitleGoBack__legends_icon {
              width: 11px;
              height: 11px;
              border-radius: 50%;
              &.owner {
                background-color: #25256f;
              }
              &.partner {
                background-color: #00a6e2;
              }
            }

            .pageTitleGoBack__legends_label {
              font-size: 14px;
              font-weight: 400;
              color: #444;
            }
          }
        }
      }
    }

    .pageTitleGoBack__icon_box {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      /* column-gap: 15px; */
      .pageTitleGoBack__icon {
        margin-right: 15px;
        width: 40px;
        height: 40px;
        &:hover {
          cursor: pointer;
        }
        &.chat {
          display: inline-block;
          position: relative;
          .chat_count {
            display: flex;
            align-items: center;
            justify-content: center;
            position: absolute;
            top: -3px;
            right: -6px;
            padding: 3px;
            min-width: 22px;
            min-height: 22px;
            background-color: ${color.blue};
            border-radius: 50%;
            font-size: 12px;
            font-weight: 500;
            color: white;
            letter-spacing: -0.5px;
          }
        }
      }
    }
  `,
};
